import { useEffect, useMemo, useState } from "react";
import { cn } from "@/lib/utils";
import { RotateCcw, Trash2, Plus, X } from "lucide-react";
import { motion, useDragControls } from "framer-motion";
import { GripHorizontal } from "lucide-react";

import { DRAW_COLORS } from "@/styles/tokens";

import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Switch } from "@/components/ui/switch";
import { Label } from "@/components/ui/label";
import { Slider } from "@/components/ui/slider";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  createDefaultCustomFibLevels,
  formatCustomFibonacciLevelValue,
  normalizeCustomFibonacciExtendData,
  calculateFibPrice,
  type FibLevel,
  type FibExtendData,
} from "@/lib/lytick/fibonacci-overlay";
import { getDrawingSnapshot, setDrawingExtendData } from "@/lib/lytick/overlay-bridge";

interface FibLevelDraft {
  id: string;
  valueInput: string;
  visible: boolean;
  color: string;
}

interface FibLevelEditorProps {
  drawingId: string;
  onClose?: () => void;
}

function buildDraftLevels(drawingId: string): FibLevelDraft[] {
  const snapshot = getDrawingSnapshot(drawingId);
  const extendData = normalizeCustomFibonacciExtendData(snapshot?.extendData);

  return extendData.levels.map((level) => ({
    id: level.id,
    valueInput: formatCustomFibonacciLevelValue(level.value, 4),
    visible: level.visible !== false,
    color: level.color ?? "",
  }));
}

function buildDraftExtend(drawingId: string): FibExtendData {
  const snapshot = getDrawingSnapshot(drawingId);
  return normalizeCustomFibonacciExtendData(snapshot?.extendData);
}

function buildFibPrice(
  drawingId: string,
  levelValue: number,
  draftExtend: FibExtendData | null
): string {
  if (!draftExtend) return "--";
  const snapshot = getDrawingSnapshot(drawingId);
  const firstPoint = snapshot?.points?.[0];
  const secondPoint = snapshot?.points?.[1];

  if (
    typeof firstPoint?.value !== "number" ||
    !Number.isFinite(firstPoint.value) ||
    typeof secondPoint?.value !== "number" ||
    !Number.isFinite(secondPoint.value)
  ) {
    return "--";
  }

  const startValue = draftExtend.reverse ? secondPoint.value : firstPoint.value;
  const endValue = draftExtend.reverse ? firstPoint.value : secondPoint.value;
  const price = calculateFibPrice(startValue, endValue, levelValue, draftExtend.fibBasedOnLogScale);
  return Number.isFinite(price) ? price.toFixed(2) : "--";
}

export function FibLevelEditor({ drawingId, onClose }: FibLevelEditorProps) {
  const [draftLevels, setDraftLevels] = useState<FibLevelDraft[]>([]);
  const [draftExtend, setDraftExtend] = useState<FibExtendData | null>(null);
  const dragControls = useDragControls();

  useEffect(() => {
    setDraftLevels(buildDraftLevels(drawingId));
    setDraftExtend(buildDraftExtend(drawingId));
  }, [drawingId]);

  const hasInvalidLevel = useMemo(
    () =>
      draftLevels.some((level) => {
        const normalized = level.valueInput.trim();
        return normalized.length === 0 || !Number.isFinite(Number(normalized));
      }),
    [draftLevels],
  );

  if (draftLevels.length === 0 || !draftExtend) {
    return null;
  }

  const updateExtend = (changes: Partial<FibExtendData>) => {
    setDraftExtend((current) => current ? { ...current, ...changes } : null);
  };

  return (
    <motion.div 
      drag 
      dragMomentum={false}
      dragListener={false}
      dragControls={dragControls}
      className="space-y-4 rounded-2xl border border-border/70 bg-background/60 p-4 w-[320px] shadow-xl backdrop-blur-xl"
    >
      <div className="flex items-center justify-between">
        <div 
          className="flex items-center gap-2 drag-handle cursor-grab active:cursor-grabbing"
          onPointerDown={(event) => {
            event.stopPropagation();
            dragControls.start(event);
          }}
          style={{ touchAction: "none" }}
        >
          <GripHorizontal className="size-4 text-muted-foreground/60" />
          <p className="text-sm font-medium text-foreground">Fibonacci Settings</p>
        </div>
        <div className="flex items-center gap-1">
          <Button
            type="button"
            variant="ghost"
            className="h-8 rounded-xl px-2 text-muted-foreground hover:text-foreground"
            onClick={() => {
              setDraftLevels(
                createDefaultCustomFibLevels().map((level) => ({
                  id: level.id,
                  valueInput: formatCustomFibonacciLevelValue(level.value, 4),
                  visible: level.visible !== false,
                  color: "",
                }))
              );
            }}
          >
            <RotateCcw className="mr-1.5 size-4" />
            Reset
          </Button>
          {onClose && (
            <Button
              type="button"
              variant="ghost"
              className="h-8 w-8 p-0 rounded-xl text-muted-foreground hover:text-foreground hover:bg-loss/10 hover:text-loss transition-colors"
              onClick={onClose}
            >
              <X className="size-4" />
            </Button>
          )}
        </div>
      </div>

      <div className="max-h-[45vh] space-y-4 overflow-y-auto pr-2">
        {/* Levels */}
        <div className="space-y-0.5 rounded-xl border border-border/60 py-1">
          {draftLevels.map((level, index) => {
            const numericValue = Number(level.valueInput.trim());
            const pricePreview = Number.isFinite(numericValue)
              ? buildFibPrice(drawingId, numericValue, draftExtend)
              : "--";

            return (
              <div key={level.id} className="flex items-center justify-between gap-2 px-2 py-1 hover:bg-white/5 transition-colors">
                <div className="flex items-center gap-2">
                  <input
                    type="checkbox"
                    checked={level.visible}
                    onChange={(event) => {
                      const checked = event.target.checked;
                      setDraftLevels((current) =>
                        current.map((item, itemIndex) =>
                          itemIndex === index ? { ...item, visible: checked } : item,
                        ),
                      );
                    }}
                    className="size-3.5 cursor-pointer rounded-sm border-border bg-transparent focus:ring-0 focus:ring-offset-0"
                  />
                  <Input
                    type="text"
                    inputMode="decimal"
                    value={level.valueInput}
                    onChange={(event) => {
                      const nextValue = event.target.value;
                      setDraftLevels((current) =>
                        current.map((item, itemIndex) =>
                          itemIndex === index ? { ...item, valueInput: nextValue } : item,
                        ),
                      );
                    }}
                    className="h-7 w-20 text-xs px-2 bg-transparent border-transparent hover:border-border focus:border-border transition-colors"
                  />
                </div>

                <div className="flex items-center gap-2">
                  <span className="text-xxs text-muted-foreground w-12 text-right tracking-tighter">
                    {pricePreview}
                  </span>
                  <input
                    type="color"
                    value={level.color || DRAW_COLORS.fib}
                    onChange={(event) => {
                      const color = event.target.value;
                      setDraftLevels((current) =>
                        current.map((item, itemIndex) =>
                          itemIndex === index ? { ...item, color } : item,
                        ),
                      );
                    }}
                    className="h-5 w-6 cursor-pointer border-none bg-transparent p-0"
                  />
                  <button
                    type="button"
                    onClick={() => {
                      setDraftLevels((current) => current.filter((_, i) => i !== index));
                    }}
                    className="text-muted-foreground/60 hover:text-loss transition-colors"
                  >
                     <Trash2 className="size-3.5" />
                  </button>
                </div>
              </div>
            );
          })}

          {/* Extension presets */}
          <div className="px-2 pb-1 pt-0.5">
            <p className="text-xxs text-muted-foreground mb-1.5">Extension presets</p>
            <div className="flex flex-wrap gap-1">
              {[1.272, 1.618, 2.0, 2.618].map((val) => {
                const alreadyAdded = draftLevels.some(
                  (l) => Math.abs(parseFloat(l.valueInput) - val) < 0.001,
                );
                return (
                  <button
                    key={val}
                    type="button"
                    disabled={alreadyAdded}
                    onClick={() =>
                      setDraftLevels((cur) => [
                        ...cur,
                        {
                          id: `fib-level-${Date.now()}-${val}`,
                          valueInput: val.toFixed(3),
                          visible: true,
                          color: "",
                        },
                      ])
                    }
                    className={cn(
                      "h-6 px-1.5 rounded border text-xxs transition-colors",
                      alreadyAdded
                        ? "opacity-40 cursor-not-allowed border-border text-muted-foreground"
                        : "border-border/60 hover:border-foreground/40 text-muted-foreground hover:text-foreground",
                    )}
                  >
                    {val.toFixed(3)}
                  </button>
                );
              })}
            </div>
          </div>

          <div className="px-2 py-2">
             <Button
               variant="ghost"
               className="h-7 w-full text-xs text-muted-foreground hover:text-foreground"
               onClick={() => {
                  setDraftLevels(current => [
                    ...current,
                    {
                      id: `fib-level-${Date.now()}`,
                      valueInput: "0.00",
                      visible: true,
                      color: ""
                    }
                  ])
               }}
             >
                <Plus className="mr-1.5 size-3" />
                Add Level
             </Button>
          </div>
        </div>

        {/* Global Options */}
        <div className="space-y-4 rounded-xl border border-border/60 p-3">
          <p className="text-xs font-semibold text-foreground/80">Trend Line</p>
          <div className="flex flex-wrap items-center gap-4">
            <div className="flex items-center space-x-2">
              <Switch 
                id="trend-line-visible" 
                checked={draftExtend.trendLine.visible}
                onCheckedChange={(v) => updateExtend({ trendLine: { ...draftExtend.trendLine, visible: v } })}
              />
              <Label htmlFor="trend-line-visible">Show</Label>
            </div>
            {draftExtend.trendLine.visible && (
              <>
                <input
                  type="color"
                  value={draftExtend.trendLine.color || DRAW_COLORS.fib}
                  onChange={(e) => updateExtend({ trendLine: { ...draftExtend.trendLine, color: e.target.value } })}
                  className="h-7 w-10 rounded border border-border bg-transparent p-1"
                />
                <Select
                  value={draftExtend.trendLine.style || 'solid'}
                  onValueChange={(v: 'solid'|'dashed'|'dotted') => updateExtend({ trendLine: { ...draftExtend.trendLine, style: v } })}
                >
                  <SelectTrigger className="h-8 w-[100px] text-xs"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="solid">Solid</SelectItem>
                    <SelectItem value="dashed">Dashed</SelectItem>
                    <SelectItem value="dotted">Dotted</SelectItem>
                  </SelectContent>
                </Select>
                <div className="flex items-center gap-2">
                   <Label className="text-xs">Thickness</Label>
                   <Input 
                      type="number" 
                      className="h-8 w-16 text-xs" 
                      min={1} max={10} step={1}
                      value={draftExtend.trendLine.size || 1.6}
                      onChange={(e) => updateExtend({ trendLine: { ...draftExtend.trendLine, size: Number(e.target.value) || 1 } })}
                    />
                </div>
              </>
            )}
          </div>
        </div>

        <div className="space-y-4 rounded-xl border border-border/60 p-3">
          <p className="text-xs font-semibold text-foreground/80">Background Fills</p>
          <div className="flex flex-col gap-4">
            <div className="flex items-center space-x-2">
              <Switch 
                id="background-visible" 
                checked={draftExtend.backgrounds.visible}
                onCheckedChange={(v) => updateExtend({ backgrounds: { ...draftExtend.backgrounds, visible: v } })}
              />
              <Label htmlFor="background-visible">Fill backgrounds</Label>
            </div>
            {draftExtend.backgrounds.visible && (
              <div className="space-y-2">
                <Label className="text-xs opacity-80">Transparency</Label>
                <Slider 
                  min={0} max={1} step={0.01} 
                  value={[draftExtend.backgrounds.alpha]}
                  onValueChange={([val]) => updateExtend({ backgrounds: { ...draftExtend.backgrounds, alpha: val } })}
                />
              </div>
            )}
          </div>
        </div>

        <div className="space-y-4 rounded-xl border border-border/60 p-3">
          <p className="text-xs font-semibold text-foreground/80">Labels & Formatting</p>
          
          <div className="flex flex-wrap gap-4">
            <div className="flex items-center space-x-2">
              <Switch 
                id="labels-prices" 
                checked={draftExtend.labels.showPrices}
                onCheckedChange={(v) => updateExtend({ labels: { ...draftExtend.labels, showPrices: v } })}
              />
              <Label htmlFor="labels-prices">Prices</Label>
            </div>
            <div className="flex items-center space-x-2">
              <Switch 
                id="labels-levels" 
                checked={draftExtend.labels.showLevels}
                onCheckedChange={(v) => updateExtend({ labels: { ...draftExtend.labels, showLevels: v } })}
              />
              <Label htmlFor="labels-levels">Levels</Label>
            </div>
          </div>

          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-1.5">
              <Label className="text-xs opacity-80">Format</Label>
              <Select
                value={draftExtend.labels.levelsFormat}
                onValueChange={(v: 'values'|'percents') => updateExtend({ labels: { ...draftExtend.labels, levelsFormat: v } })}
              >
                <SelectTrigger className="h-8"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="values">Values</SelectItem>
                  <SelectItem value="percents">Percents</SelectItem>
                </SelectContent>
              </Select>
            </div>
            
            <div className="space-y-1.5">
              <Label className="text-xs opacity-80">Horizontal</Label>
              <Select
                value={draftExtend.labels.position}
                onValueChange={(v: 'left'|'center'|'right') => updateExtend({ labels: { ...draftExtend.labels, position: v } })}
              >
                <SelectTrigger className="h-8"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="left">Left</SelectItem>
                  <SelectItem value="center">Center</SelectItem>
                  <SelectItem value="right">Right</SelectItem>
                </SelectContent>
              </Select>
            </div>

            <div className="space-y-1.5">
              <Label className="text-xs opacity-80">Vertical</Label>
              <Select
                value={draftExtend.labels.align}
                onValueChange={(v: 'top'|'middle'|'bottom') => updateExtend({ labels: { ...draftExtend.labels, align: v } })}
              >
                <SelectTrigger className="h-8"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="top">Top</SelectItem>
                  <SelectItem value="middle">Middle</SelectItem>
                  <SelectItem value="bottom">Bottom</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
        </div>

        <div className="space-y-3 rounded-xl border border-border/60 p-3">
          <p className="text-xs font-semibold text-foreground/80">Extensions & Advanced</p>
          
          <div className="flex flex-col gap-3">
            <div className="flex items-center space-x-2">
              <Switch 
                id="extend-left" 
                checked={draftExtend.extendLeft}
                onCheckedChange={(v) => updateExtend({ extendLeft: v })}
              />
              <Label htmlFor="extend-left">Extend Lines Left</Label>
            </div>
            
            <div className="flex items-center space-x-2">
              <Switch 
                id="extend-right" 
                checked={draftExtend.extendRight}
                onCheckedChange={(v) => updateExtend({ extendRight: v })}
              />
              <Label htmlFor="extend-right">Extend Lines Right</Label>
            </div>

            <div className="flex items-center space-x-2">
              <Switch 
                id="reverse" 
                checked={draftExtend.reverse}
                onCheckedChange={(v) => updateExtend({ reverse: v })}
              />
              <Label htmlFor="reverse">Reverse</Label>
            </div>

            <div className="flex items-center space-x-2">
              <Switch 
                id="log-scale" 
                checked={draftExtend.fibBasedOnLogScale}
                onCheckedChange={(v) => updateExtend({ fibBasedOnLogScale: v })}
              />
              <Label htmlFor="log-scale">Fib Levels based on Log Scale</Label>
            </div>
          </div>
        </div>
      </div>

      <Button
        type="button"
        className="w-full rounded-xl mt-4"
        disabled={hasInvalidLevel}
        onClick={() => {
          if (hasInvalidLevel) {
            return;
          }

          const levels: FibLevel[] = draftLevels.map((level, index) => ({
            id: level.id || `fib-level-${index}`,
            value: Number(level.valueInput.trim()),
            visible: level.visible,
            color: level.color || undefined,
          }));

          setDrawingExtendData(drawingId, {
            ...draftExtend,
            levels,
          });
        }}
      >
        Apply
      </Button>
    </motion.div>
  );
}
